<script setup lang="ts">
/**
 * PAGE CAGNOTTE — dons fermés (Karim 2026-10-15) : terminée, en pause ou
 * encore en brouillon.
 *
 * La nouvelle page (12/10) avait perdu ce que faisait l'ancienne carte
 * (CampaignDonationCard : « Cagnotte clôturée » / « en pause ») : sur
 * ordinateur, le choix du montant et « Faire un don » restaient actifs sur
 * une cagnotte terminée ; sur téléphone, la barre de don disparaissait sans
 * rien dire.
 *
 * Choix de Karim : le bloc de don reste EN PLACE, voilé (flou + gris,
 * inactif), et ce voile givré pose l'état par-dessus — sans fond coloré.
 * Le visiteur repart avec une suite : la page de l'association.
 * Sert à la carte (ordinateur) et à la barre du bas (téléphone, `compact`) ;
 * le parent doit être positionné (relative / fixed).
 */
export type EtatDonCagnotte = "terminee" | "pause" | "brouillon";

const props = defineProps<{
  etat: EtatDonCagnotte;
  /** Jour de fin en clair (« 31 octobre 2026 »), pour une cagnotte terminée. */
  dateFin?: string | null;
  /** Slug de l'association : « Voir l'association ». */
  slugAssociation?: string | null;
  compact?: boolean;
}>();

const { t } = useI18n();
const localePath = useLocalePath();

const contenu = computed(() => {
  switch (props.etat) {
    case "terminee":
      return {
        icone: "i-lucide-flag",
        titre: t("campagne.pageCagnotte.etatTermineeTitre"),
        texte: props.dateFin
          ? t("campagne.pageCagnotte.etatTermineeTexteDate", { date: props.dateFin })
          : t("campagne.pageCagnotte.etatTermineeTexte"),
      };
    case "pause":
      return {
        icone: "i-lucide-pause",
        titre: t("campagne.pageCagnotte.etatPauseTitre"),
        texte: t("campagne.pageCagnotte.etatPauseTexte"),
      };
    default:
      return {
        icone: "i-lucide-file-edit",
        titre: t("campagne.pageCagnotte.etatBrouillonTitre"),
        texte: t("campagne.pageCagnotte.etatBrouillonTexte"),
      };
  }
});

/** Un brouillon n'est vu qu'en aperçu, par l'association : pas de lien. */
const lienAssociation = computed(() =>
  props.slugAssociation && props.etat !== "brouillon"
    ? localePath({ path: `/associations/${props.slugAssociation}` })
    : null,
);
</script>

<template>
  <!-- ORDINATEUR : voile givré sur toute la zone de don, état centré. -->
  <div
    v-if="!compact"
    role="status"
    data-testid="cagnotte-etat-don"
    class="absolute inset-0 flex flex-col items-center justify-center px-8 text-center bg-gradient-to-b from-white/50 via-white/85 to-white/50 backdrop-blur-[6px]"
  >
    <span class="flex size-12 items-center justify-center rounded-full bg-(--color-ink) text-white shadow-[0_8px_20px_-6px_rgba(0,0,0,0.35)]" aria-hidden="true">
      <UIcon :name="contenu.icone" class="size-5" />
    </span>
    <p class="mt-4 text-[19px] leading-tight text-(--color-ink)" style="font-weight: 700 !important">{{ contenu.titre }}</p>
    <p class="mt-1.5 max-w-[270px] text-[14px] leading-snug text-gray-600">{{ contenu.texte }}</p>
    <a
      v-if="lienAssociation"
      :href="lienAssociation"
      class="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white px-5 py-2.5 text-sm text-(--color-ink) no-underline ring-1 ring-gray-300 transition-colors hover:bg-gray-50"
      style="font-weight: 600 !important"
    >
      {{ $t("campagne.pageCagnotte.etatVoirAssociation") }}
      <UIcon name="i-lucide-arrow-right" class="size-4" />
    </a>
  </div>

  <!-- TÉLÉPHONE : même voile sur la barre du bas, état sur une ligne. -->
  <div
    v-else
    role="status"
    data-testid="cagnotte-etat-don"
    class="absolute inset-0 flex items-center gap-3 px-4 bg-white/80 backdrop-blur-[6px]"
  >
    <span class="flex size-10 shrink-0 items-center justify-center rounded-full bg-(--color-ink) text-white" aria-hidden="true">
      <UIcon :name="contenu.icone" class="size-5" />
    </span>
    <div class="min-w-0 flex-1">
      <p class="text-[15px] leading-tight text-(--color-ink)" style="font-weight: 700 !important">{{ contenu.titre }}</p>
      <p class="mt-0.5 text-[13px] leading-snug text-gray-600 line-clamp-3">{{ contenu.texte }}</p>
    </div>
    <a
      v-if="lienAssociation"
      :href="lienAssociation"
      class="flex size-10 shrink-0 items-center justify-center rounded-full bg-white text-(--color-ink) ring-1 ring-gray-300"
      :aria-label="$t('campagne.pageCagnotte.etatVoirAssociation')"
      :title="$t('campagne.pageCagnotte.etatVoirAssociation')"
    >
      <UIcon name="i-lucide-arrow-right" class="size-4" />
    </a>
  </div>
</template>
